// Pure logic for the Sections panel (owner-editable blocks). Edits are whole-
// document transforms through @dunea/plugin-sdk's applyLayoutOps, saved with
// the version the panel loaded (studio refuses stale writes with 409).
import type { FieldDefinition } from '@dunea/plugin-sdk';
import {
  applyLayoutOps,
  emptySectionsFile,
  layoutInstanceIds,
  sectionAccepts,
  type BlockInstance,
  type LayoutIssue,
  type LayoutOp,
  type LibraryBlock,
  type PageType,
  type SectionsFile,
} from '~/lib/plugin-sdk-layout';

export const PAGE_TYPE_LABELS: Record<PageType, string> = {
  home: 'Home page',
  page: 'Page',
  product: 'Product page',
  collection: 'Collection page',
  cart: 'Cart',
  blog: 'Blog',
  article: 'Article',
  funnel_step: 'Funnel step',
  any: 'Any page',
};

export const SOURCE_LABELS: Record<LibraryBlock['source'], string> = {
  local: 'Yours',
  theme: 'Theme',
  marketplace: 'Plugin',
};

export function libraryIndex(blocks: readonly LibraryBlock[]): Map<string, LibraryBlock> {
  return new Map(blocks.map((b) => [b.type, b]));
}

/** Groups (pages), home first, then alphabetical. */
export function groupIds(doc: SectionsFile): string[] {
  return Object.keys(doc.groups ?? {}).sort((a, b) => (a === 'home' ? -1 : b === 'home' ? 1 : a.localeCompare(b)));
}

/** "about-us" → "About us"; "home" → "Home". */
export function groupLabel(id: string): string {
  const words = id.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

/** A group id from a name the owner typed ("About us" → "about-us"), or null when it can't be one. */
export function groupIdFromName(name: string, taken: readonly string[]): string | null {
  const id = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  if (!id) {
    return null;
  }

  if (!taken.includes(id)) {
    return id;
  }

  for (let n = 2; n < 100; n++) {
    if (!taken.includes(`${id}-${n}`)) {
      return `${id}-${n}`;
    }
  }

  return null;
}

export interface SectionRow {
  instance: BlockInstance;
  index: number;
  block: LibraryBlock | null;
  title: string;
  hidden: boolean;
  /** The block accepts children. */
  acceptsBlocks: boolean;
  childCount: number;
  /** Errors from the last validation that point into this section. */
  issues: LayoutIssue[];
}

export function sectionRows(
  doc: SectionsFile,
  group: string,
  library: ReadonlyMap<string, LibraryBlock>,
  issues: readonly LayoutIssue[] = [],
): SectionRow[] {
  return (doc.groups[group]?.sections ?? []).map((instance, index) => {
    const block = library.get(instance.type) ?? null;
    const prefix = `groups.${group}.sections.${index}`;

    return {
      instance,
      index,
      block,
      title: block?.schema.title ?? instance.type,
      hidden: Boolean(instance.hidden),
      acceptsBlocks: Boolean(block && block.schema.kind === 'section' && block.schema.accepts.length > 0),
      childCount: instance.blocks?.length ?? 0,
      issues: issues.filter((i) => i.path === prefix || i.path.startsWith(`${prefix}.`)),
    };
  });
}

/** Sections the owner can add to a group of `pageType` (all page types for 'any'). */
export function addableSections(library: readonly LibraryBlock[], pageType: PageType): LibraryBlock[] {
  return library
    .filter((b) => b.schema.kind === 'section' && b.hasCode)
    .filter((b) => pageType === 'any' || b.schema.pageTypes.includes('any') || b.schema.pageTypes.includes(pageType))
    .sort((a, b) => sourceRank(a) - sourceRank(b) || a.schema.title.localeCompare(b.schema.title));
}

function sourceRank(b: LibraryBlock): number {
  return b.source === 'local' ? 0 : b.source === 'theme' ? 1 : 2;
}

/** Child block types a section accepts. */
export function addableChildren(section: LibraryBlock, library: readonly LibraryBlock[]): LibraryBlock[] {
  return library.filter((b) => b.schema.kind === 'block' && b.hasCode && sectionAccepts(section, b.type));
}

export interface EmbedRow {
  block: LibraryBlock;
  enabled: boolean;
  settings: Record<string, unknown>;
}

export function embedRows(doc: SectionsFile, library: readonly LibraryBlock[]): EmbedRow[] {
  return library
    .filter((b) => b.schema.kind === 'embed' && b.hasCode)
    .map((block) => {
      const e = doc.embeds?.[block.type];
      return { block, enabled: Boolean(e?.enabled), settings: e?.settings ?? {} };
    });
}

/** Apply ops; throws with the first op error (the panel only issues ops it knows are valid). */
export function edit(doc: SectionsFile | null, ops: LayoutOp[], library: readonly LibraryBlock[]): SectionsFile {
  const { document, errors } = applyLayoutOps(doc ?? emptySectionsFile(), ops, library);

  if (errors.length) {
    throw new Error(errors[0]);
  }

  return document;
}

/** Move an instance one step up (-1) or down (+1) among its siblings; null at the edge. */
export function stepOp(siblings: readonly BlockInstance[], id: string, delta: -1 | 1): LayoutOp | null {
  const i = siblings.findIndex((s) => s.id === id);
  const j = i + delta;

  return i === -1 || j < 0 || j >= siblings.length ? null : { op: 'move', id, index: j };
}

/** The form's values: saved settings over the block's defaults (what the site actually shows). */
export function formValues(block: LibraryBlock | null, settings: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...settings };

  for (const def of block?.schema.settings ?? []) {
    if (out[def.key] === undefined && def.default !== undefined) {
      out[def.key] = def.default;
    }
  }

  return out;
}

/** Settings to save: unset (undefined) values dropped so the block's default applies again. */
export function settingsToSave(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined));
}

export function settingFields(block: LibraryBlock | null): FieldDefinition[] {
  return (block?.schema.settings ?? []) as FieldDefinition[];
}

export function hasInstance(doc: SectionsFile, id: string): boolean {
  return layoutInstanceIds(doc).has(id);
}

// ─── errors ───

interface ApiErr {
  response?: { status?: number; data?: { code?: string; message?: unknown; errors?: LayoutIssue[]; version?: string } };
}

export type SaveProblem =
  | { kind: 'conflict' }
  | { kind: 'asleep' }
  | { kind: 'invalid'; issues: LayoutIssue[]; message: string }
  | { kind: 'other'; message: string };

export function classifyError(err: unknown, fallback: string): SaveProblem {
  const res = (err as ApiErr | null)?.response;
  const message = typeof res?.data?.message === 'string' && res.data.message.trim() ? res.data.message : fallback;

  if (res?.status === 409 || res?.data?.code === 'conflict') {
    return { kind: 'conflict' };
  }

  if (res?.status === 503 || res?.data?.code === 'sandbox_not_running') {
    return { kind: 'asleep' };
  }

  if (res?.status === 422 || res?.data?.code === 'invalid') {
    return { kind: 'invalid', issues: Array.isArray(res?.data?.errors) ? res.data.errors : [], message };
  }

  return { kind: 'other', message };
}

/** "groups.home.sections.0.settings.heading: Heading is required" → "Heading is required". */
export function issueText(issue: LayoutIssue): string {
  return issue.message;
}
